/* ============================================================
   BUMP AUDIO — synthesized per-scene SOUNDTRACK engine.
   ------------------------------------------------------------
   Sibling to src/engine/audio.ts (TVAudio). Where TVAudio makes the
   *set's* chrome noises (power relay, channel thunk, static hiss),
   BumpAudio scores the *bumps themselves* — the music + foley that
   plays under an ident/station/ad while it's on screen.

   Same philosophy as TVAudio: one shared AudioContext, lazily
   created and resumed on a user gesture, and every sound generated
   at runtime from oscillators + filtered noise + gain envelopes.
   NO audio asset files are ever loaded — the whole soundtrack is math.

   Signal routing (per scene):
       sound nodes ─► sceneBus ─► master ─► toneLP ─► toneShelf ─► limiter ─► out
   The sceneBus is swapped on every scene (beginScene/endScene) so a
   hard cut — or a replay — cleanly silences whatever was still ringing.

   This module has NO orion imports, so it is fully portable. The
   scores table (scores.ts) and the BumpRunner glue (see AGENTS.md)
   are what bind it to the bump system.
   ============================================================ */

/* ----------------------------- public types ----------------------------- */

export interface Vibrato {
  rate?: number
  depth?: number
}

export interface FilterSpec {
  type: BiquadFilterType
  freq: number
  Q?: number
}

export interface VoiceOpts {
  type?: OscillatorType
  freq?: number
  glideTo?: number
  glideCurve?: 'exp' | 'lin'
  dur?: number
  attack?: number
  release?: number
  hold?: number
  gain?: number
  detune?: number
  pan?: number
  vibrato?: Vibrato | null
  /** extra additive partials as [freqMultiplier, gainMultiplier] pairs. */
  partials?: Array<[number, number]> | null
  /** absolute AudioContext time to start at; defaults to now(). */
  time?: number
  /** route to a node other than the current scene bus (rare). */
  out?: AudioNode
}

export interface NoiseOpts {
  dur?: number
  attack?: number
  release?: number
  gain?: number
  filter?: FilterSpec
  sweepTo?: number
  pan?: number
  time?: number
  out?: AudioNode
}

/** A one-shot voice you can cut short. */
export interface VoiceHandle {
  stop(at?: number): void
}

/** A sustained sound (drone/pad/noiseBed) — keep the handle to stop/duck it. */
export interface SustainHandle {
  stop(rel?: number): void
  set(gain: number, ramp?: number): void
}

/** A running rhythmic loop (pulse) — keep the handle to stop it. */
export interface PulseHandle {
  stop(): void
}

type NoteName = string | number

/* ----------------------------- module state ----------------------------- */

let AC: AudioContext | null = null
let master: GainNode | null = null
let limiter: DynamicsCompressorNode | null = null
let toneLP: BiquadFilterNode | null = null
let toneShelf: BiquadFilterNode | null = null
let noiseBuf: AudioBuffer | null = null
let bus: GainNode | null = null
let unlocked = false
let muted = false
let masterVol = 0.85

/* ----------------------------- note → Hz ----------------------------- */
const SEMI: Record<string, number> = {
  c: 0, 'c#': 1, db: 1, d: 2, 'd#': 3, eb: 3, e: 4, f: 5,
  'f#': 6, gb: 6, g: 7, 'g#': 8, ab: 8, a: 9, 'a#': 10, bb: 10, b: 11,
}

/** Note name ("C4", "f#5", "Bb2") or raw Hz → frequency in Hz. */
export function note(name: NoteName): number {
  if (typeof name === 'number') return name
  const m = String(name).trim().toLowerCase().match(/^([a-g][#b]?)(-?\d+)$/)
  if (!m) return 440
  const midi = (parseInt(m[2], 10) + 1) * 12 + SEMI[m[1]]
  return 440 * Math.pow(2, (midi - 69) / 12)
}
export const n = note

/* ----------------------------- helpers ----------------------------- */

function makeNoise(ac: AudioContext, seconds = 2): AudioBuffer {
  const len = Math.floor(ac.sampleRate * seconds)
  const buf = ac.createBuffer(1, len, ac.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
  return buf
}

function ensure(): AudioContext | null {
  if (AC) return AC
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  AC = new Ctor()
  limiter = AC.createDynamicsCompressor()
  limiter.threshold.value = -8
  limiter.knee.value = 6
  limiter.ratio.value = 12
  limiter.attack.value = 0.003
  limiter.release.value = 0.18
  // master tone-shaping — warms the whole mix so nothing is piercing:
  //   master ─► gentle low-pass ─► high-shelf cut ─► limiter ─► out
  toneLP = AC.createBiquadFilter()
  toneLP.type = 'lowpass'
  toneLP.frequency.value = 6200
  toneLP.Q.value = 0.4
  toneShelf = AC.createBiquadFilter()
  toneShelf.type = 'highshelf'
  toneShelf.frequency.value = 3200
  toneShelf.gain.value = -6
  master = AC.createGain()
  master.gain.value = muted ? 0 : masterVol
  master.connect(toneLP)
  toneLP.connect(toneShelf)
  toneShelf.connect(limiter)
  limiter.connect(AC.destination)
  noiseBuf = makeNoise(AC, 2)
  return AC
}

const ready = (): boolean => unlocked && !!AC && AC.state === 'running' && !!bus
const now = (): number => (AC ? AC.currentTime : 0)
const destOf = (opts?: { out?: AudioNode }): AudioNode => (opts && opts.out) || bus || master!

// A panner (cheap stereo) — returns null if unsupported.
function panNode(pan?: number): StereoPannerNode | null {
  if (pan == null || !AC) return null
  try {
    const p = AC.createStereoPanner()
    p.pan.value = Math.max(-1, Math.min(1, pan))
    return p
  } catch {
    return null
  }
}

// Connect a chain of (possibly null) nodes left→right; returns the live ones.
function chain(nodes: Array<AudioNode | null>): AudioNode[] {
  const live = nodes.filter(Boolean) as AudioNode[]
  for (let i = 0; i < live.length - 1; i++) live[i].connect(live[i + 1])
  return live
}

/* --------------------------- primitives --------------------------- */

// Generic enveloped oscillator voice.
function voice(opts: VoiceOpts = {}): VoiceHandle | undefined {
  if (!ready() || !AC) return
  const {
    type = 'sine', freq = 440, glideTo, glideCurve = 'exp',
    dur = 0.3, attack = 0.005, release = 0.12, hold = 0,
    gain = 0.5, detune = 0, pan, vibrato, partials,
  } = opts
  const t0 = opts.time != null ? opts.time : now()
  const osc = AC.createOscillator()
  osc.type = type
  osc.detune.value = detune
  osc.frequency.setValueAtTime(Math.max(8, freq), t0)
  if (glideTo != null) {
    const target = Math.max(8, glideTo)
    if (glideCurve === 'exp') osc.frequency.exponentialRampToValueAtTime(target, t0 + dur)
    else osc.frequency.linearRampToValueAtTime(target, t0 + dur)
  }
  const g = AC.createGain()
  const peak = gain
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t0 + attack)
  const sustainUntil = t0 + attack + hold + dur
  g.gain.setValueAtTime(Math.max(0.0002, peak), Math.max(t0 + attack, sustainUntil - release))
  g.gain.exponentialRampToValueAtTime(0.0001, sustainUntil + release)
  // Soften the edgy waveforms: roll the bright upper harmonics off
  // sawtooth/square voices (the main source of "sharpness") with a
  // note-relative low-pass, so they stay warm without losing character.
  let tone: BiquadFilterNode | null = null
  if (type === 'sawtooth' || type === 'square') {
    const ref = Math.max(freq, glideTo != null ? glideTo : freq)
    tone = AC.createBiquadFilter()
    tone.type = 'lowpass'
    tone.frequency.value = Math.min(5200, Math.max(1500, ref * 3.5))
    tone.Q.value = 0.5
  }
  const p = panNode(pan)
  chain([osc, tone, g, p, destOf(opts)])
  // vibrato
  if (vibrato) {
    const lfo = AC.createOscillator()
    lfo.frequency.value = vibrato.rate || 6
    const lfoGain = AC.createGain()
    lfoGain.gain.value = vibrato.depth || 4
    lfo.connect(lfoGain)
    lfoGain.connect(osc.frequency)
    lfo.start(t0)
    lfo.stop(sustainUntil + release + 0.05)
  }
  // optional extra partials (cheap additive)
  if (partials) {
    for (const [mult, amp] of partials) {
      voice({
        ...opts, partials: null, freq: freq * mult,
        glideTo: glideTo != null ? glideTo * mult : undefined,
        gain: peak * amp, vibrato: null,
      })
    }
  }
  osc.start(t0)
  osc.stop(sustainUntil + release + 0.08)
  return {
    stop(at?: number) {
      const t = at || now()
      try {
        g.gain.cancelScheduledValues(t)
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.1)
        osc.stop(t + 0.15)
      } catch {
        /* already stopped */
      }
    },
  }
}

// Filtered noise burst.
function noiseBurst(opts: NoiseOpts = {}): { node: BiquadFilterNode } | undefined {
  if (!ready() || !AC) return
  const {
    dur = 0.3, attack = 0.004, release = 0.12, gain = 0.4,
    filter = { type: 'bandpass', freq: 1200, Q: 0.8 },
    sweepTo, pan,
  } = opts
  const t0 = opts.time != null ? opts.time : now()
  const src = AC.createBufferSource()
  src.buffer = noiseBuf
  src.loop = true
  const bq = AC.createBiquadFilter()
  bq.type = filter.type
  bq.frequency.setValueAtTime(Math.max(20, filter.freq), t0)
  if (sweepTo != null) bq.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t0 + dur)
  bq.Q.value = filter.Q != null ? filter.Q : 0.8
  const g = AC.createGain()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack)
  g.gain.setValueAtTime(Math.max(0.0002, gain), t0 + attack + dur)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + dur + release)
  const p = panNode(pan)
  chain([src, bq, g, p, destOf(opts)])
  src.start(t0)
  src.stop(t0 + attack + dur + release + 0.05)
  return { node: bq }
}

/* ============================ THE ENGINE ============================ */

export const BumpAudio = {
  /* ---- lifecycle ---- */

  /** Create + resume the AudioContext. MUST be called from a user gesture
   *  (browser autoplay policy). Until then every synth fn no-ops. Pair with
   *  TVAudio.resume() on the same gesture. */
  unlock(): void {
    if (!ensure()) return
    unlocked = true
    if (AC && AC.state !== 'running') void AC.resume()
  },
  isUnlocked(): boolean {
    return unlocked && !!AC && AC.state === 'running'
  },
  /** Matches TVAudio.isReady() — context exists. */
  isReady(): boolean {
    return !!AC
  },
  setMuted(v: boolean): void {
    muted = !!v
    if (master) master.gain.setTargetAtTime(muted ? 0 : masterVol, now(), 0.02)
  },
  isMuted(): boolean {
    return muted
  },
  setVolume(v: number): void {
    masterVol = Math.max(0, Math.min(1, v))
    if (master && !muted) master.gain.setTargetAtTime(masterVol, now(), 0.02)
  },
  getVolume(): number {
    return masterVol
  },
  now,
  note,

  /* ---- scene bus management (called by BumpRunner) ---- */

  /** Open a fresh per-scene bus. Call when a bump's scene is shown. */
  beginScene(): GainNode | undefined {
    if (!unlocked || !AC) return
    if (bus) this.endScene()
    bus = AC.createGain()
    bus.gain.value = 1
    bus.connect(master!)
    return bus
  },
  /** Fade + drop the current scene bus. Call on dispose/swap/replay. */
  endScene(): void {
    if (!bus || !AC) {
      bus = null
      return
    }
    const b = bus
    bus = null
    const t = now()
    try {
      b.gain.cancelScheduledValues(t)
      b.gain.setValueAtTime(b.gain.value, t)
      b.gain.linearRampToValueAtTime(0, t + 0.07)
    } catch {
      /* noop */
    }
    setTimeout(() => {
      try {
        b.disconnect()
      } catch {
        /* noop */
      }
    }, 150)
  },
  /** Duck (target<1) or restore (target=1) the live scene bus, e.g. while a
   *  finished bump is held on its last frame. */
  fadeBus(target = 1, ramp = 0.4): void {
    if (!bus || !AC) return
    try {
      bus.gain.setTargetAtTime(target, now(), ramp)
    } catch {
      /* noop */
    }
  },

  /* ---- one-shots (low level) ---- */
  tone: voice,
  noise: noiseBurst,

  /* ---- one-shots (foley kit) ---- */

  blip(o: { freq?: number; dur?: number; type?: OscillatorType; gain?: number; time?: number; pan?: number } = {}): void {
    const { freq = 880, dur = 0.05, type = 'square', gain = 0.25, time, pan } = o
    voice({ type, freq, dur, attack: 0.002, release: 0.04, gain, time, pan })
  },

  tick(o: { freq?: number; gain?: number; time?: number; pan?: number } = {}): void {
    const { freq = 320, gain = 0.3, time, pan } = o
    noiseBurst({ dur: 0.02, release: 0.03, gain, filter: { type: 'bandpass', freq, Q: 1.2 }, time, pan })
    voice({ type: 'sine', freq: freq * 0.8, glideTo: freq * 0.4, dur: 0.04, attack: 0.001, release: 0.05, gain: gain * 0.6, time })
  },

  impact(o: { gain?: number; time?: number; freq?: number; body?: number } = {}): void {
    const { gain = 0.9, time, freq = 150, body = 0.9 } = o
    const t = time != null ? time : now()
    voice({ type: 'sine', freq, glideTo: freq * 0.28, dur: 0.18, attack: 0.001, release: 0.2, gain: gain * body, time: t })
    voice({ type: 'triangle', freq: freq * 0.5, glideTo: freq * 0.2, dur: 0.22, attack: 0.001, release: 0.26, gain: gain * 0.5, time: t })
    noiseBurst({ dur: 0.06, release: 0.12, gain: gain * 0.7, filter: { type: 'lowpass', freq: 2400, Q: 0.6 }, sweepTo: 400, time: t })
  },

  boom(o: { gain?: number; time?: number; freq?: number } = {}): void {
    const { gain = 1.0, time, freq = 90 } = o
    const t = time != null ? time : now()
    voice({ type: 'sine', freq, glideTo: freq * 0.4, dur: 0.5, attack: 0.004, release: 0.7, gain, time: t })
    voice({ type: 'sine', freq: freq * 1.5, glideTo: freq * 0.6, dur: 0.3, attack: 0.004, release: 0.4, gain: gain * 0.4, time: t })
    noiseBurst({ dur: 0.2, release: 0.5, gain: gain * 0.35, filter: { type: 'lowpass', freq: 600 }, time: t })
  },

  riser(o: { dur?: number; from?: number; to?: number; gain?: number; time?: number } = {}): void {
    const { dur = 1.2, from = 200, to = 5000, gain = 0.3, time } = o
    noiseBurst({ dur, attack: 0.3, release: 0.18, gain, filter: { type: 'bandpass', freq: from, Q: 4 }, sweepTo: to, time })
  },

  whoosh(o: { dur?: number; gain?: number; time?: number; pan?: number } = {}): void {
    const { dur = 0.35, gain = 0.4, time, pan = 0 } = o
    noiseBurst({ dur, attack: 0.08, release: 0.18, gain, filter: { type: 'bandpass', freq: 500, Q: 1.4 }, sweepTo: 3000, time, pan })
    noiseBurst({ dur, attack: 0.08, release: 0.2, gain: gain * 0.7, filter: { type: 'bandpass', freq: 3000, Q: 1.4 }, sweepTo: 400, time: (time != null ? time : now()) + dur * 0.5, pan: -pan })
  },

  zap(o: { gain?: number; time?: number; from?: number; to?: number } = {}): void {
    const { gain = 0.4, time, from = 1800, to = 120 } = o
    voice({ type: 'sawtooth', freq: from, glideTo: to, dur: 0.16, attack: 0.001, release: 0.08, gain, time })
  },

  sparkle(o: { count?: number; base?: number; spread?: number; dur?: number; gain?: number; time?: number } = {}): void {
    const { count = 8, base = 1400, spread = 1800, dur = 0.7, gain = 0.18, time } = o
    const t0 = time != null ? time : now()
    for (let i = 0; i < count; i++) {
      const f = base + Math.random() * spread
      voice({ type: 'sine', freq: f, dur: 0.12, attack: 0.001, release: 0.18, gain: gain * (0.5 + Math.random() * 0.5), time: t0 + Math.random() * dur, partials: [[2.01, 0.3]] })
    }
  },

  bell(o: { freq?: number; dur?: number; gain?: number; time?: number } = {}): void {
    const { freq = note('A5'), dur = 0.8, gain = 0.3, time } = o
    voice({ type: 'sine', freq, dur, attack: 0.002, release: dur, gain, time, partials: [[2.0, 0.45], [3.01, 0.18], [4.7, 0.08]] })
  },

  chord(o: { notes?: NoteName[]; dur?: number; type?: OscillatorType; gain?: number; attack?: number; release?: number; time?: number; detune?: number } = {}): void {
    const { notes = ['C4', 'E4', 'G4'], dur = 0.9, type = 'sawtooth', gain = 0.22, attack = 0.01, release = 0.5, time, detune = 6 } = o
    const t = time != null ? time : now()
    notes.forEach((nm, i) => {
      voice({ type, freq: note(nm), dur, attack, release, gain, time: t, detune: i % 2 ? detune : -detune })
    })
  },

  arp(o: { notes?: NoteName[]; step?: number; dur?: number; type?: OscillatorType; gain?: number; time?: number } = {}): void {
    const { notes = ['C4', 'E4', 'G4', 'C5'], step = 0.12, dur = 0.16, type = 'triangle', gain = 0.3, time } = o
    const t0 = time != null ? time : now()
    notes.forEach((nm, i) => voice({ type, freq: note(nm), dur, attack: 0.003, release: 0.12, gain, time: t0 + i * step, partials: [[2, 0.25]] }))
  },

  coin(o: { gain?: number; time?: number } = {}): void {
    const { gain = 0.3, time } = o
    const t = time != null ? time : now()
    voice({ type: 'square', freq: note('B5'), dur: 0.07, attack: 0.002, release: 0.05, gain, time: t })
    voice({ type: 'square', freq: note('E6'), dur: 0.22, attack: 0.002, release: 0.18, gain, time: t + 0.08 })
  },

  powerup(o: { gain?: number; time?: number } = {}): void {
    const { gain = 0.26, time } = o
    this.arp({ notes: ['C5', 'E5', 'G5', 'C6', 'E6'], step: 0.06, dur: 0.1, type: 'square', gain, time })
  },

  sadTrombone(o: { gain?: number; time?: number } = {}): void {
    const { gain = 0.3, time } = o
    const t0 = time != null ? time : now()
    const seq = [note('D3'), note('C3'), note('B2'), note('Bb2')]
    seq.forEach((f, i) => voice({ type: 'sawtooth', freq: f, glideTo: f * 0.94, dur: 0.32, attack: 0.02, release: 0.12, gain, time: t0 + i * 0.26, vibrato: { rate: 5, depth: 6 } }))
  },

  heartbeat(o: { gain?: number; time?: number; freq?: number } = {}): void {
    const { gain = 0.6, time, freq = 70 } = o
    const t = time != null ? time : now()
    voice({ type: 'sine', freq, glideTo: freq * 0.6, dur: 0.12, attack: 0.004, release: 0.18, gain, time: t })
    voice({ type: 'sine', freq, glideTo: freq * 0.6, dur: 0.12, attack: 0.004, release: 0.22, gain: gain * 0.8, time: t + 0.28 })
  },

  // "talking" announcer rhythm — formant-ish blips, no words (legal mumble, PA).
  vox(o: { syllables?: number; base?: number; gain?: number; step?: number; time?: number; bright?: number } = {}): void {
    const { syllables = 5, base = 200, gain = 0.16, step = 0.13, time, bright = 700 } = o
    const t0 = time != null ? time : now()
    for (let i = 0; i < syllables; i++) {
      const f = base * (0.85 + Math.random() * 0.4)
      const dur = 0.06 + Math.random() * 0.05
      noiseBurst({ dur, attack: 0.01, release: 0.05, gain, filter: { type: 'bandpass', freq: bright * (0.7 + Math.random() * 0.8), Q: 5 }, time: t0 + i * step })
      voice({ type: 'sawtooth', freq: f, dur, attack: 0.01, release: 0.06, gain: gain * 0.5, time: t0 + i * step })
    }
  },

  /* ---- drums ---- */
  kick(o: { gain?: number; time?: number; freq?: number } = {}): void {
    const { gain = 0.8, time, freq = 150 } = o
    voice({ type: 'sine', freq, glideTo: 45, dur: 0.12, attack: 0.001, release: 0.18, gain, time })
  },
  snare(o: { gain?: number; time?: number } = {}): void {
    const { gain = 0.5, time } = o
    noiseBurst({ dur: 0.04, release: 0.14, gain, filter: { type: 'highpass', freq: 1400, Q: 0.7 }, time })
    voice({ type: 'triangle', freq: 220, glideTo: 160, dur: 0.06, attack: 0.001, release: 0.1, gain: gain * 0.4, time })
  },
  hat(o: { gain?: number; time?: number; open?: boolean } = {}): void {
    const { gain = 0.25, time, open = false } = o
    noiseBurst({ dur: open ? 0.12 : 0.02, release: open ? 0.14 : 0.04, gain, filter: { type: 'highpass', freq: 7000, Q: 0.7 }, time })
  },

  /* ---- sustained beds (return a SustainHandle) ---- */

  drone(o: { freq?: number; type?: OscillatorType; gain?: number; fifth?: boolean; sub?: boolean; vibrato?: Vibrato } = {}): SustainHandle {
    const { freq = note('C2'), type = 'sawtooth', gain = 0.12, fifth = true, sub = true, vibrato } = o
    if (!ready() || !AC) return { stop() {}, set() {} }
    const ac = AC
    const t0 = now()
    const out = ac.createGain()
    out.gain.setValueAtTime(0.0001, t0)
    out.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + 1.2)
    const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1400; lp.Q.value = 0.6
    out.connect(lp); lp.connect(destOf())
    const oscs: OscillatorNode[] = []
    const add = (f: number, g: number, ty?: OscillatorType) => {
      const o2 = ac.createOscillator(); o2.type = ty || type; o2.frequency.value = f
      const og = ac.createGain(); og.gain.value = g
      o2.connect(og); og.connect(out); o2.start(t0); oscs.push(o2)
      if (vibrato) {
        const lfo = ac.createOscillator(); lfo.frequency.value = vibrato.rate || 0.2
        const lg = ac.createGain(); lg.gain.value = vibrato.depth || 2
        lfo.connect(lg); lg.connect(o2.frequency); lfo.start(t0); oscs.push(lfo)
      }
    }
    add(freq, 0.5)
    if (sub) add(freq / 2, 0.4, 'sine')
    if (fifth) add(freq * 1.5, 0.25)
    return {
      stop(rel = 0.6) {
        const t = now()
        try { out.gain.cancelScheduledValues(t); out.gain.setValueAtTime(out.gain.value, t); out.gain.exponentialRampToValueAtTime(0.0001, t + rel) } catch { /* noop */ }
        setTimeout(() => oscs.forEach((o2) => { try { o2.stop() } catch { /* noop */ } }), (rel + 0.1) * 1000)
      },
      set(g: number, ramp = 0.3) { try { out.gain.setTargetAtTime(g, now(), ramp) } catch { /* noop */ } },
    }
  },

  noiseBed(o: { gain?: number; filter?: FilterSpec; attack?: number; lfo?: { rate?: number; depth?: number } } = {}): SustainHandle {
    const { gain = 0.06, filter = { type: 'lowpass', freq: 900, Q: 0.5 }, attack = 1.0, lfo } = o
    if (!ready() || !AC) return { stop() {}, set() {} }
    const ac = AC
    const t0 = now()
    const src = ac.createBufferSource(); src.buffer = noiseBuf; src.loop = true
    const bq = ac.createBiquadFilter(); bq.type = filter.type; bq.frequency.value = filter.freq; bq.Q.value = filter.Q || 0.5
    const g = ac.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack)
    src.connect(bq); bq.connect(g); g.connect(destOf())
    src.start(t0)
    let lfoOsc: OscillatorNode | undefined
    if (lfo) {
      lfoOsc = ac.createOscillator(); lfoOsc.frequency.value = lfo.rate || 0.15
      const lg = ac.createGain(); lg.gain.value = lfo.depth || 200
      lfoOsc.connect(lg); lg.connect(bq.frequency); lfoOsc.start(t0)
    }
    return {
      stop(rel = 0.6) {
        const t = now()
        try { g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.exponentialRampToValueAtTime(0.0001, t + rel) } catch { /* noop */ }
        setTimeout(() => { try { src.stop() } catch { /* noop */ } try { lfoOsc?.stop() } catch { /* noop */ } }, (rel + 0.1) * 1000)
      },
      set(gv: number, ramp = 0.3) { try { g.gain.setTargetAtTime(gv, now(), ramp) } catch { /* noop */ } },
    }
  },

  pad(o: { notes?: NoteName[]; gain?: number; type?: OscillatorType; attack?: number; cutoff?: number } = {}): SustainHandle {
    const { notes = ['C3', 'G3', 'E4'], gain = 0.08, type = 'sawtooth', attack = 1.4, cutoff = 1600 } = o
    if (!ready() || !AC) return { stop() {}, set() {} }
    const ac = AC
    const t0 = now()
    const out = ac.createGain(); out.gain.setValueAtTime(0.0001, t0); out.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack)
    const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = cutoff; lp.Q.value = 0.7
    out.connect(lp); lp.connect(destOf())
    const oscs: OscillatorNode[] = []
    notes.forEach((nm) => {
      const f = note(nm)
      ;[0, 1].forEach((d) => {
        const o2 = ac.createOscillator(); o2.type = type; o2.frequency.value = f; o2.detune.value = d ? 7 : -7
        const og = ac.createGain(); og.gain.value = 0.4 / notes.length
        o2.connect(og); og.connect(out); o2.start(t0); oscs.push(o2)
      })
    })
    return {
      stop(rel = 0.9) {
        const t = now()
        try { out.gain.cancelScheduledValues(t); out.gain.setValueAtTime(out.gain.value, t); out.gain.exponentialRampToValueAtTime(0.0001, t + rel) } catch { /* noop */ }
        setTimeout(() => oscs.forEach((o2) => { try { o2.stop() } catch { /* noop */ } }), (rel + 0.1) * 1000)
      },
      set(g: number, ramp = 0.4) { try { out.gain.setTargetAtTime(g, now(), ramp) } catch { /* noop */ } },
    }
  },

  // simple looping rhythmic pulse. onBeat(beat) fires each step (capture the engine from the
  // enclosing cue's `A` if you need it — avoids a self-referential type on the singleton).
  pulse(o: { bpm?: number; swing?: number; onBeat?: (beat: number) => void } = {}): PulseHandle {
    const { bpm = 120, swing = 0, onBeat } = o
    if (!ready()) return { stop() {} }
    let beat = 0
    let alive = true
    const spb = 60 / bpm
    let timer: ReturnType<typeof setTimeout>
    const tick = () => {
      if (!alive) return
      try { onBeat?.(beat) } catch { /* noop */ }
      beat++
      const extra = swing && beat % 2 ? swing * spb : 0
      timer = setTimeout(tick, (spb + extra) * 1000)
    }
    timer = setTimeout(tick, 0)
    return { stop() { alive = false; clearTimeout(timer) } }
  },
}

/** The public shape of the engine singleton (cf. TVAudioApi). */
export type BumpAudioApi = typeof BumpAudio

export default BumpAudio
